/**
 * PilotPage — OpenTender AI pilot program briefing dossier.
 *
 * Design direction: a private briefing document, not a landing page. Uses
 * OpenTender brand tokens (Plus Jakarta Sans, Manrope, navy #000052, white
 * accent) but a completely different layout vocabulary from the
 * public marketing page: inverted navy hero, reading-column prose,
 * editorial marginalia, a distinct layout primitive per section (split-
 * screen, ledger, timeline, chapter index).
 *
 * Posts feedback to existing POST /contact/submit.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { createGlobalStyle, keyframes, css } from 'styled-components';

/* ──────────────────────────────────────────────────────────────────────────
 * Brand tokens (from opentender.ai)
 * ────────────────────────────────────────────────────────────────────── */
const c = {
  primary: '#000052',
  primaryDeep: '#000033',
  primaryHover: '#00003D',
  primarySoft: 'rgba(0, 0, 82, 0.06)',
  primaryFade: 'rgba(0, 0, 82, 0.10)',

  heading: '#111111',
  body: '#696969',
  secondary: '#333333',
  muted: '#94928C',

  white: '#FFFFFF',
  paper: '#F8FAFB',
  panel: '#F0F2F4',

  border: 'rgba(16, 16, 16, 0.10)',
  borderSoft: 'rgba(16, 16, 16, 0.06)',

  coral: '#FFFFFF',
  coralSoft: 'rgba(255, 255, 255, 0.1)',

  onDarkBody: 'rgba(255, 255, 255, 0.72)',
  onDarkMuted: 'rgba(255, 255, 255, 0.5)',
  onDarkBorder: 'rgba(255, 255, 255, 0.14)',
};

const heading = `'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif`;
const body = `'Manrope', -apple-system, BlinkMacSystemFont, sans-serif`;

/* ──────────────────────────────────────────────────────────────────────────
 * Animations
 * ────────────────────────────────────────────────────────────────────── */
const riseIn = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const drawLine = keyframes`
  from { stroke-dashoffset: 400; }
  to   { stroke-dashoffset: 0; }
`;

/* ──────────────────────────────────────────────────────────────────────────
 * Page global
 * ────────────────────────────────────────────────────────────────────── */
const PageGlobal = createGlobalStyle`
  body.pilot-dossier {
    background: ${c.white};
  }
  /* Selection on light backgrounds: navy highlight, white text */
  body.pilot-dossier ::selection {
    background: ${c.primary};
    color: ${c.white};
  }
  /* Selection on navy sections: white highlight, navy text — keeps contrast either way */
  body.pilot-dossier .on-navy ::selection,
  body.pilot-dossier header ::selection {
    background: ${c.white};
    color: ${c.primary};
  }
`;

/* ──────────────────────────────────────────────────────────────────────────
 * Shell
 * ────────────────────────────────────────────────────────────────────── */
const PageWrap = styled.div`
  min-height: 100vh;
  background: ${c.white};
  color: ${c.body};
  font-family: ${body};
  line-height: 1.7;
  overflow-x: hidden;
`;

/* ──────────────────────────────────────────────────────────────────────────
 * Thin top strip — document metadata (not a nav)
 * ────────────────────────────────────────────────────────────────────── */
const TopStrip = styled.div`
  background: ${c.primary};
  color: ${c.onDarkMuted};
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const TopStripInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 14px 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  font-family: ${body};
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  font-weight: 500;

  @media (max-width: 720px) {
    padding: 12px 18px;
    font-size: 10px;
  }

  .left { display: flex; align-items: center; gap: 20px; }

  .logo {
    display: inline-flex;
    align-items: center;
    img {
      height: 22px;
      display: block;
      /* logo is dark navy on transparent; invert luminance so it reads on the navy top strip */
      filter: brightness(0) invert(1);
    }
  }

  .sep {
    width: 1px;
    height: 16px;
    background: ${c.onDarkBorder};
  }

  .dot {
    width: 6px;
    height: 6px;
    background: ${c.coral};
    border-radius: 50%;
  }
  a {
    color: ${c.white} !important;
    text-decoration: none !important;
    transition: color 160ms ease;
    &:hover { color: ${c.coral} !important; }
  }
`;

/* ──────────────────────────────────────────────────────────────────────────
 * Navy hero band
 * ────────────────────────────────────────────────────────────────────── */
const Hero = styled.header`
  background: ${c.primary};
  color: ${c.white};
  padding: 80px 32px 120px;
  position: relative;
  overflow: hidden;

  /* subtle diagonal accent — a thin coral rule top-right, purely decorative */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 260px;
    height: 2px;
    background: ${c.coral};
    opacity: 0.9;
  }

  @media (max-width: 720px) { padding: 56px 20px 80px; }
`;

const HeroGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(280px, 1fr) minmax(0, 700px) 1fr;
  gap: 48px;
  align-items: start;

  @media (max-width: 1080px) { grid-template-columns: 1fr; gap: 20px; }
`;

const HeroEdition = styled.div`
  font-family: ${heading};
  color: ${c.onDarkMuted};
  font-size: clamp(72px, 10vw, 180px);
  font-weight: 200;
  line-height: 0.85;
  letter-spacing: -0.04em;
  -webkit-text-stroke: 1.5px rgba(255, 255, 255, 0.35);
  color: transparent;
  padding-top: 12px;
  user-select: none;
  animation: ${riseIn} 900ms cubic-bezier(0.2, 0.8, 0.2, 1) 150ms both;

  .sub {
    display: block;
    font-size: 11px;
    letter-spacing: 0.24em;
    text-transform: uppercase;
    color: ${c.onDarkMuted};
    -webkit-text-stroke: 0;
    font-weight: 600;
    margin-top: 16px;
    padding-left: 2px;
  }

  @media (max-width: 1080px) { font-size: 80px; }
  @media (max-width: 720px) { font-size: 60px; }
`;

const HeroKicker = styled.div`
  font-family: ${body};
  font-size: 12px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${c.coral};
  font-weight: 600;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  animation: ${riseIn} 900ms cubic-bezier(0.2, 0.8, 0.2, 1) 200ms both;

  &::before {
    content: '';
    width: 40px;
    height: 1px;
    background: ${c.coral};
  }
`;

const HeroTitle = styled.h1`
  font-family: ${heading};
  color: ${c.white};
  font-weight: 700;
  font-size: clamp(40px, 5.4vw, 72px);
  line-height: 1.05;
  letter-spacing: -0.025em;
  margin: 0 0 32px;
  animation: ${riseIn} 900ms cubic-bezier(0.2, 0.8, 0.2, 1) 280ms both;

  .hl {
    position: relative;
    white-space: nowrap;
    display: inline-block;
  }
  .hl svg {
    position: absolute;
    left: -4px;
    right: -4px;
    bottom: -14px;
    width: calc(100% + 8px);
    height: 16px;
    pointer-events: none;
    overflow: visible;
  }
  .hl svg path {
    fill: none;
    stroke: ${c.coral};
    stroke-width: 3;
    stroke-linecap: round;
    stroke-dasharray: 420;
    stroke-dashoffset: 420;
    animation: ${drawLine} 1400ms cubic-bezier(0.2, 0.8, 0.2, 1) 1000ms both;
  }
`;

const HeroLede = styled.p`
  font-family: ${body};
  color: ${c.onDarkBody};
  font-size: clamp(16px, 1.4vw, 18px);
  line-height: 1.7;
  max-width: 620px;
  margin: 0 0 40px;
  animation: ${riseIn} 900ms cubic-bezier(0.2, 0.8, 0.2, 1) 360ms both;

  em { color: ${c.white}; font-style: italic; }
`;

const HeroMeta = styled.div`
  font-family: ${body};
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${c.onDarkMuted};
  font-weight: 500;
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  padding-top: 20px;
  border-top: 1px solid ${c.onDarkBorder};
  max-width: 620px;
  animation: ${riseIn} 900ms cubic-bezier(0.2, 0.8, 0.2, 1) 440ms both;

  span { display: inline-flex; align-items: center; gap: 8px; }
  .dot {
    width: 4px;
    height: 4px;
    background: ${c.coral};
    border-radius: 50%;
  }
`;

/* ──────────────────────────────────────────────────────────────────────────
 * Section marker — "§ 01 ─── THE PROSPECTUS"
 * ────────────────────────────────────────────────────────────────────── */
const SectionMarker = styled.div<{ $onDark?: boolean }>`
  font-family: ${body};
  font-size: 11px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  font-weight: 600;
  color: ${p => (p.$onDark ? c.coral : c.primary)};
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 40px;

  .num {
    font-family: ${heading};
    font-weight: 700;
    font-size: 13px;
    letter-spacing: -0.01em;
    color: ${p => (p.$onDark ? c.white : c.heading)};
  }

  .rule {
    flex: 1;
    height: 1px;
    background: ${p => (p.$onDark ? c.onDarkBorder : c.border)};
    max-width: 120px;
  }
`;

/* ──────────────────────────────────────────────────────────────────────────
 * Section scaffolding — reading column vs. section slot
 * ────────────────────────────────────────────────────────────────────── */
const Section = styled.section<{ $variant?: 'paper' | 'navy' }>`
  background: ${p => (p.$variant === 'navy' ? c.primary : p.$variant === 'paper' ? c.paper : c.white)};
  color: ${p => (p.$variant === 'navy' ? c.white : c.body)};
  padding: 100px 0;
  scroll-margin-top: 80px;
  position: relative;

  @media (max-width: 720px) { padding: 64px 0; }
`;

const Reading = styled.div`
  max-width: 680px;
  margin: 0 auto;
  padding: 0 32px;

  @media (max-width: 720px) { padding: 0 20px; }
`;

const Wide = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 32px;

  @media (max-width: 720px) { padding: 0 20px; }
`;

const SectionH2 = styled.h2<{ $onDark?: boolean }>`
  font-family: ${heading};
  color: ${p => (p.$onDark ? c.white : c.heading)};
  font-weight: 700;
  font-size: clamp(32px, 3.6vw, 48px);
  line-height: 1.08;
  letter-spacing: -0.025em;
  margin: 0 0 28px;

  em {
    font-style: normal;
    position: relative;
    display: inline-block;
    color: ${p => (p.$onDark ? c.white : c.heading)};
  }
  em::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -6px;
    height: 10px;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 10' preserveAspectRatio='none'><path d='M2,7 C50,1 100,10 198,3' fill='none' stroke='%23FFFFFF' stroke-width='2.5' stroke-linecap='round'/></svg>");
    background-repeat: no-repeat;
    background-size: 100% 100%;
  }
`;

const Lede = styled.p<{ $onDark?: boolean }>`
  font-family: ${body};
  color: ${p => (p.$onDark ? c.onDarkBody : c.body)};
  font-size: 17px;
  line-height: 1.7;
  margin: 0 0 22px;

  strong { color: ${p => (p.$onDark ? c.white : c.heading)}; font-weight: 700; }
  em { color: ${p => (p.$onDark ? c.white : c.heading)}; font-style: italic; }
`;

const DropCap = styled.p`
  font-family: ${body};
  color: ${c.body};
  font-size: 18px;
  line-height: 1.72;
  margin: 0 0 24px;

  &::first-letter {
    font-family: ${heading};
    font-size: 5em;
    line-height: 0.88;
    font-weight: 700;
    float: left;
    padding: 8px 14px 0 0;
    color: ${c.primary};
  }

  em { color: ${c.heading}; font-style: italic; }
  strong { color: ${c.heading}; font-weight: 700; }
`;

/* Chapter opener — big outlined numeral + explainer, side by side */
const SectionIntro = styled.div<{ $onDark?: boolean }>`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 48px;
  align-items: start;
  margin-bottom: 56px;
  padding-bottom: 44px;
  border-bottom: 1px solid ${p => (p.$onDark ? c.onDarkBorder : c.border)};

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    gap: 20px;
    margin-bottom: 40px;
    padding-bottom: 32px;
  }
`;

const IntroNum = styled.div<{ $onDark?: boolean }>`
  font-family: ${heading};
  font-size: clamp(100px, 11vw, 160px);
  font-weight: 200;
  line-height: 0.85;
  letter-spacing: -0.05em;
  color: transparent;
  -webkit-text-stroke: 1.5px
    ${p => (p.$onDark ? 'rgba(255, 255, 255, 0.38)' : 'rgba(0, 0, 82, 0.25)')};
  padding-top: 2px;
  user-select: none;
  min-width: 180px;
  font-variant-numeric: tabular-nums;

  @media (max-width: 720px) {
    font-size: 96px;
    min-width: auto;
  }
`;

const IntroText = styled.p<{ $onDark?: boolean }>`
  font-family: ${body};
  font-size: clamp(16px, 1.15vw, 18px);
  line-height: 1.6;
  color: ${p => (p.$onDark ? c.onDarkBody : c.secondary)};
  max-width: 520px;
  margin: 0;
  padding-top: 14px;
  font-weight: 400;

  em {
    color: ${p => (p.$onDark ? c.white : c.heading)};
    font-style: italic;
  }
`;

/* ──────────────────────────────────────────────────────────────────────────
 * § 02 — split-screen fit check
 * ────────────────────────────────────────────────────────────────────── */
const SplitWrap = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 620px;

  @media (max-width: 840px) { grid-template-columns: 1fr; min-height: auto; }
`;

const SplitPane = styled.div<{ $variant: 'fit' | 'unfit' }>`
  padding: 80px 56px;
  background: ${p => (p.$variant === 'fit' ? c.white : c.primary)};
  color: ${p => (p.$variant === 'fit' ? c.heading : c.white)};
  position: relative;

  &::before {
    content: ${p => (p.$variant === 'fit' ? "'YES'" : "'NO'")};
    position: absolute;
    top: 28px;
    right: 40px;
    font-family: ${heading};
    font-size: 140px;
    line-height: 1;
    font-weight: 800;
    letter-spacing: -0.06em;
    color: ${p => (p.$variant === 'fit' ? c.primary : c.coral)};
    opacity: 0.08;
    pointer-events: none;
    user-select: none;
  }

  @media (max-width: 720px) { padding: 56px 32px; &::before { font-size: 100px; right: 24px; top: 20px; } }
  @media (max-width: 480px) { padding: 44px 22px; }
`;

const SplitTitle = styled.h3<{ $onDark?: boolean }>`
  font-family: ${heading};
  color: ${p => (p.$onDark ? c.white : c.heading)};
  font-weight: 700;
  font-size: clamp(24px, 2.6vw, 32px);
  letter-spacing: -0.02em;
  margin: 0 0 8px;
`;

const SplitIntro = styled.p<{ $onDark?: boolean }>`
  font-family: ${body};
  color: ${p => (p.$onDark ? c.onDarkBody : c.body)};
  font-size: 15px;
  margin: 0 0 36px;
  max-width: 380px;
  line-height: 1.6;
`;

const SplitList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SplitItem = styled.li<{ $onDark?: boolean }>`
  display: grid;
  grid-template-columns: 40px 1fr;
  gap: 16px;
  padding-top: 18px;
  border-top: 1px solid ${p => (p.$onDark ? c.onDarkBorder : c.border)};

  &:first-child { border-top: none; padding-top: 0; }

  .idx {
    font-family: ${heading};
    font-weight: 700;
    font-size: 13px;
    color: ${p => (p.$onDark ? c.coral : c.primary)};
    font-variant-numeric: tabular-nums;
    padding-top: 2px;
  }

  h4 {
    font-family: ${heading};
    font-weight: 700;
    font-size: 17px;
    line-height: 1.3;
    letter-spacing: -0.015em;
    color: ${p => (p.$onDark ? c.white : c.heading)};
    margin: 0 0 6px;
  }

  p {
    font-family: ${body};
    color: ${p => (p.$onDark ? c.onDarkBody : c.body)};
    font-size: 14.5px;
    line-height: 1.55;
    margin: 0;
  }
`;

/* ──────────────────────────────────────────────────────────────────────────
 * § 03 — ledger / T-account
 * ────────────────────────────────────────────────────────────────────── */
const Ledger = styled.div`
  display: grid;
  grid-template-columns: 1fr 1px 1fr;
  max-width: 1000px;
  margin: 0 auto;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    .divider { display: none; }
  }
`;

const LedgerDivider = styled.div`
  background: ${c.border};
  width: 1px;
  grid-row: span 3;

  @media (max-width: 720px) { display: none; }
`;

const LedgerCol = styled.div`
  padding: 0 36px;

  @media (max-width: 720px) {
    padding: 0;
    & + & { margin-top: 56px; padding-top: 56px; border-top: 1px solid ${c.border}; }
  }
`;

const LedgerHead = styled.div`
  font-family: ${body};
  font-size: 11px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${c.primary};
  font-weight: 700;
  margin-bottom: 4px;
`;

const LedgerSub = styled.div`
  font-family: ${heading};
  font-weight: 700;
  font-size: 22px;
  letter-spacing: -0.015em;
  color: ${c.heading};
  margin-bottom: 32px;
`;

const LedgerList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    display: grid;
    grid-template-columns: 28px 1fr;
    gap: 14px;
    padding: 16px 0;
    border-bottom: 1px dashed ${c.borderSoft};
    font-family: ${body};
    color: ${c.secondary};
    font-size: 15px;
    line-height: 1.55;

    &:last-child { border-bottom: none; }

    .mark {
      font-family: ${heading};
      font-size: 13px;
      font-weight: 700;
      color: ${c.primary};
      padding-top: 1px;
    }
    strong { color: ${c.heading}; font-weight: 700; }
  }
`;

/* ──────────────────────────────────────────────────────────────────────────
 * § 04 — horizontal timeline
 * ────────────────────────────────────────────────────────────────────── */
const Timeline = styled.ol`
  list-style: none;
  padding: 0;
  margin: 48px 0 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  position: relative;

  /* connecting line */
  &::before {
    content: '';
    position: absolute;
    top: 24px;
    left: 12%;
    right: 12%;
    height: 1px;
    background:
      repeating-linear-gradient(
        to right,
        ${c.primary} 0,
        ${c.primary} 6px,
        transparent 6px,
        transparent 14px
      );
  }

  @media (max-width: 840px) {
    grid-template-columns: 1fr;
    gap: 40px;
    &::before {
      top: 0;
      bottom: 0;
      left: 24px;
      width: 1px;
      height: auto;
      right: auto;
      background:
        repeating-linear-gradient(
          to bottom,
          ${c.primary} 0,
          ${c.primary} 6px,
          transparent 6px,
          transparent 14px
        );
    }
  }
`;

const Station = styled.li`
  position: relative;
  padding-top: 68px;

  .node {
    position: absolute;
    top: 0;
    left: 0;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: ${c.white};
    border: 2px solid ${c.primary};
    color: ${c.primary};
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: ${heading};
    font-weight: 800;
    font-size: 15px;
    letter-spacing: -0.01em;
    z-index: 1;
    transition: all 200ms ease;
  }

  &:hover .node {
    background: ${c.primary};
    color: ${c.white};
    transform: scale(1.08);
  }

  .chip {
    display: inline-block;
    font-family: ${body};
    font-size: 10px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    font-weight: 700;
    color: ${c.coral};
    margin-bottom: 12px;
  }

  h4 {
    font-family: ${heading};
    color: ${c.heading};
    font-weight: 700;
    font-size: 22px;
    line-height: 1.25;
    letter-spacing: -0.02em;
    margin: 0 0 10px;
    max-width: 280px;
  }

  p {
    font-family: ${body};
    color: ${c.body};
    font-size: 14.5px;
    line-height: 1.6;
    margin: 0;
    max-width: 300px;
  }

  @media (max-width: 840px) {
    padding-top: 0;
    padding-left: 72px;
    .node { top: -2px; }
  }
`;

/* ──────────────────────────────────────────────────────────────────────────
 * § 05 — walkthrough: chapter index + numbered prose
 * ────────────────────────────────────────────────────────────────────── */
const VideoFrame = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%;
  background: ${c.primary};
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 72px;

  .thumb {
    position: absolute;
    inset: 0;
    background-size: cover;
    background-position: center;
  }

  /* subtle dark overlay to lift the play button + reduce thumbnail glare */
  .shade {
    position: absolute;
    inset: 0;
    background:
      linear-gradient(180deg, rgba(0, 0, 82, 0.25) 0%, rgba(0, 0, 82, 0.55) 100%);
    transition: background 300ms ease;
  }
  button.poster:hover + .overlay .shade,
  button.poster:focus + .overlay .shade {
    background: linear-gradient(180deg, rgba(0, 0, 82, 0.35) 0%, rgba(0, 0, 82, 0.65) 100%);
  }

  .overlay {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 20px;
    color: ${c.onDarkBody};
    font-family: ${body};
    font-size: 12px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    pointer-events: none;
    text-shadow: 0 1px 8px rgba(0, 0, 0, 0.3);
  }

  button.poster {
    position: absolute;
    inset: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    z-index: 2;
  }

  .play {
    width: 84px;
    height: 84px;
    border-radius: 50%;
    background: ${c.white};
    color: ${c.primary};
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 240ms cubic-bezier(0.2, 0.8, 0.2, 1), background 200ms ease;
    box-shadow: 0 20px 48px -10px rgba(0, 0, 0, 0.5);
  }
  button.poster:hover .play,
  button.poster:focus .play {
    transform: scale(1.1);
    background: ${c.primary};
    color: ${c.white};
  }

  .corner {
    position: absolute;
    top: 16px;
    left: 16px;
    font-family: ${body};
    font-size: 10px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: ${c.white};
    font-weight: 600;
    text-shadow: 0 1px 8px rgba(0, 0, 0, 0.4);
    z-index: 3;
    pointer-events: none;
  }

  iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

const ContentsBlock = styled.div`
  max-width: 680px;
  margin: 0 auto 80px;
  padding: 36px 0;
  border-top: 2px solid ${c.primary};
  border-bottom: 2px solid ${c.primary};

  .label {
    font-family: ${body};
    font-size: 11px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: ${c.primary};
    font-weight: 700;
    margin-bottom: 24px;
  }
`;

const ContentsList = styled.ol`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ContentsRow = styled.li`
  display: grid;
  grid-template-columns: 40px 1fr auto;
  gap: 16px;
  align-items: baseline;
  padding: 8px 0;
  font-family: ${heading};
  color: ${c.heading};
  font-size: 17px;
  font-weight: 500;

  .roman {
    color: ${c.primary};
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  .title {
    position: relative;
    &::after {
      content: '';
      position: absolute;
      bottom: 6px;
      left: calc(100% + 8px);
      right: -8px;
      border-bottom: 1px dotted ${c.border};
    }
  }

  .page {
    color: ${c.muted};
    font-family: ${body};
    font-size: 13px;
    letter-spacing: 0.08em;
    font-weight: 500;
    text-transform: uppercase;
    flex-shrink: 0;
  }
`;

const Chapter = styled.article`
  max-width: 680px;
  margin: 0 auto 72px;

  &:last-child { margin-bottom: 0; }

  .chapnum {
    font-family: ${heading};
    color: ${c.coral};
    font-weight: 700;
    font-size: 13px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    margin-bottom: 12px;
  }

  h3 {
    font-family: ${heading};
    color: ${c.heading};
    font-weight: 700;
    font-size: clamp(26px, 2.8vw, 34px);
    line-height: 1.15;
    letter-spacing: -0.02em;
    margin: 0 0 20px;
  }

  p {
    font-family: ${body};
    color: ${c.body};
    font-size: 17px;
    line-height: 1.72;
    margin: 0 0 18px;
  }

  strong { color: ${c.heading}; font-weight: 700; }

  ul {
    margin: 16px 0;
    padding-left: 20px;
    color: ${c.body};
    font-size: 15.5px;
    line-height: 1.65;
    li { margin-bottom: 6px; }
    li::marker { color: ${c.coral}; }
  }
`;

/* ──────────────────────────────────────────────────────────────────────────
 * § 06 — Q&A dialogue
 * ────────────────────────────────────────────────────────────────────── */
const QA = styled.div`
  max-width: 680px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 36px;
`;

const QABlock = styled.div`
  display: grid;
  grid-template-columns: 48px 1fr;
  gap: 16px;

  & + & {
    padding-top: 36px;
    border-top: 1px solid ${c.border};
  }

  .q, .a {
    font-family: ${heading};
    font-weight: 800;
    font-size: 13px;
    letter-spacing: 0.1em;
    padding-top: 4px;
  }
  .q { color: ${c.coral}; }
  .a { color: ${c.primary}; }

  .content {
    font-family: ${body};
    color: ${c.body};
    font-size: 16px;
    line-height: 1.7;

    h4 {
      font-family: ${heading};
      font-weight: 700;
      font-size: 19px;
      color: ${c.heading};
      line-height: 1.35;
      letter-spacing: -0.015em;
      margin: 0 0 4px;
    }
    em { color: ${c.heading}; font-style: italic; }
  }
`;

/* ──────────────────────────────────────────────────────────────────────────
 * § 07 — feedback (navy closing band)
 * ────────────────────────────────────────────────────────────────────── */
const FormCard = styled.form`
  max-width: 720px;
  margin: 0 auto;
  background: ${c.white};
  border-radius: 4px;
  padding: 48px 44px 40px;
  box-shadow: 0 32px 72px -20px rgba(0, 0, 0, 0.5);

  @media (max-width: 640px) { padding: 32px 22px; }
`;

const FormGrid = styled.div`
  display: grid;
  gap: 26px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

const FieldLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-family: ${body};
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-weight: 700;
  color: ${c.heading};
`;

const fieldBase = css`
  font-family: ${body};
  font-size: 15px;
  padding: 12px 2px 10px;
  border: none;
  border-bottom: 1px solid ${c.border};
  background: transparent;
  color: ${c.heading};
  transition: border-color 160ms ease;
  outline: none;
  text-transform: none;
  letter-spacing: normal;
  font-weight: 400;

  &:focus { border-bottom-color: ${c.primary}; }
  &::placeholder { color: ${c.muted}; }
`;

const Field = styled.input`${fieldBase}`;
const TextArea = styled.textarea`
  ${fieldBase}
  resize: vertical;
  min-height: 120px;
  line-height: 1.55;
`;

const RadioRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  text-transform: none;
  letter-spacing: normal;
`;

const RadioChip = styled.label<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 10px 16px;
  border: 1px solid ${p => (p.$active ? c.primary : c.border)};
  background: ${p => (p.$active ? c.primary : c.white)};
  color: ${p => (p.$active ? c.white : c.heading)};
  font-family: ${body};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 160ms ease;
  user-select: none;
  text-transform: none;
  letter-spacing: normal;

  input { display: none; }
  &:hover { border-color: ${c.primary}; }
`;

const StarRow = styled.div` display: flex; gap: 4px; `;

const Star = styled.button<{ $filled: boolean }>`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: ${p => (p.$filled ? c.primary : c.border)};
  transition: color 140ms ease, transform 140ms ease;
  &:hover { color: ${c.primary}; transform: scale(1.1); }
`;

const SubmitBtn = styled.button`
  justify-self: start;
  font-family: ${body};
  font-size: 12px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  font-weight: 700;
  background: ${c.primary};
  color: ${c.white};
  border: none;
  padding: 16px 32px;
  cursor: pointer;
  transition: background 160ms ease;
  display: inline-flex;
  align-items: center;
  gap: 12px;

  &::after {
    content: '—→';
    letter-spacing: 0;
    transition: transform 160ms ease;
  }
  &:hover:not(:disabled) {
    background: ${c.coral};
    &::after { transform: translateX(4px); }
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const FormFoot = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;

  .hint {
    font-family: ${body};
    font-size: 11px;
    letter-spacing: 0.08em;
    color: ${c.body};
    text-transform: none;
  }
`;

const ErrorLine = styled.div`
  font-family: ${body};
  color: ${c.coral};
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0;
  text-transform: none;
`;

const SuccessBlock = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: 56px 36px;
  background: ${c.white};
  border-radius: 4px;
  text-align: center;

  .mark {
    font-family: ${heading};
    color: ${c.coral};
    font-weight: 800;
    font-size: 12px;
    letter-spacing: 0.26em;
    margin-bottom: 20px;
  }

  h3 {
    font-family: ${heading};
    font-weight: 700;
    font-size: 28px;
    color: ${c.heading};
    letter-spacing: -0.02em;
    line-height: 1.2;
    margin: 0 0 12px;
  }
  p {
    font-family: ${body};
    color: ${c.body};
    font-size: 15.5px;
    max-width: 460px;
    margin: 0 auto;
  }
`;

/* ──────────────────────────────────────────────────────────────────────────
 * § 08 — Survey CTA card
 * ────────────────────────────────────────────────────────────────────── */
const SurveyCard = styled.div`
  max-width: 820px;
  margin: 0 auto;
  padding: 52px 48px;
  background: ${c.white};
  border: 1px solid ${c.border};
  border-radius: 4px;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 40px;
  align-items: center;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    gap: 28px;
    padding: 36px 28px;
    text-align: center;
  }

  .copy h4 {
    font-family: ${heading};
    color: ${c.heading};
    font-weight: 700;
    font-size: clamp(22px, 2.2vw, 28px);
    line-height: 1.2;
    letter-spacing: -0.02em;
    margin: 0 0 10px;
  }

  .copy p {
    font-family: ${body};
    color: ${c.body};
    font-size: 15.5px;
    line-height: 1.6;
    margin: 0;
  }

  .meta {
    font-family: ${body};
    font-size: 11px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: ${c.muted};
    margin-top: 14px;
    font-weight: 600;

    span { margin-right: 14px; }
    span::before { content: '·'; color: ${c.border}; margin-right: 14px; }
    span:first-child::before { content: ''; margin-right: 0; }
  }
`;

const SurveyCta = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 18px 28px;
  background: ${c.primary};
  color: ${c.white};
  font-family: ${body};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  text-decoration: none;
  border-radius: 4px;
  transition: background 200ms ease, transform 200ms ease;
  white-space: nowrap;

  /* Overrides the app-wide global a:hover that would tint the text navy (invisible on navy bg) */
  &,
  &:hover,
  &:focus,
  &:visited {
    color: ${c.white} !important;
    text-decoration: none !important;
  }

  .arrow {
    transition: transform 200ms ease;
    font-size: 14px;
    letter-spacing: 0;
  }

  &:hover {
    background: ${c.primaryDeep};
    .arrow { transform: translateX(6px); }
  }
`;

/* §04 station 01 primary CTA — only actionable button on the page.
   Modeled on SurveyCta but slightly tighter (sits inside a station card,
   not a standalone section), and uses Plus Jakarta Sans for the heading
   feel since it terminates a numbered station rather than a section. */
const RequestAccessCta = styled.div`
  margin-top: 18px;

  a {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 12px 22px;
    background: ${c.primary};
    color: ${c.white};
    font-family: ${heading};
    font-size: 14px;
    font-weight: 600;
    letter-spacing: -0.005em;
    text-decoration: none;
    border-radius: 4px;
    transition: background 200ms ease, transform 200ms ease;
    white-space: nowrap;
  }

  /* Override app-wide a:hover that would tint text navy (invisible on navy bg) */
  a, a:hover, a:focus, a:visited {
    color: ${c.white} !important;
    text-decoration: none !important;
  }

  a .arrow {
    transition: transform 200ms ease;
    font-size: 15px;
    letter-spacing: 0;
  }

  a:hover {
    background: ${c.primaryHover};
    transform: translateY(-1px);
    .arrow { transform: translateX(5px); }
  }

  @media (max-width: 720px) {
    a { width: 100%; justify-content: center; }
  }
`;

/* §06 outro — pilot users with questions/concerns/anything land here.
   Lives inside §06 (white section), so the card uses paper bg + soft stroke
   to differentiate from the QA list above. Two-column on desktop (copy /
   CTA), single-column at <720px. */
const TalkToHuman = styled.aside`
  margin: 72px auto 0;
  max-width: 820px;
  padding: 36px 40px;
  background: ${c.paper};
  border: 1px solid ${c.border};
  border-radius: 4px;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 40px;
  align-items: center;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    gap: 24px;
    padding: 28px 24px;
  }

  .label {
    font-family: ${body};
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: ${c.muted};
    margin-bottom: 12px;
  }

  h4 {
    font-family: ${heading};
    color: ${c.heading};
    font-weight: 700;
    font-size: clamp(22px, 2.2vw, 26px);
    line-height: 1.2;
    letter-spacing: -0.02em;
    margin: 0 0 10px;
  }

  p {
    font-family: ${body};
    color: ${c.body};
    font-size: 15.5px;
    line-height: 1.6;
    margin: 0;
    max-width: 520px;
  }

  a.cta {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 14px 22px;
    background: ${c.primary};
    color: ${c.white};
    font-family: ${heading};
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    border-radius: 4px;
    white-space: nowrap;
    transition: background 200ms ease, transform 200ms ease;
  }

  a.cta, a.cta:hover, a.cta:focus, a.cta:visited {
    color: ${c.white} !important;
    text-decoration: none !important;
  }

  a.cta .arrow {
    transition: transform 200ms ease;
    font-size: 15px;
  }

  a.cta:hover {
    background: ${c.primaryHover};
    transform: translateY(-1px);
    .arrow { transform: translateX(5px); }
  }

  @media (max-width: 720px) {
    a.cta { width: 100%; justify-content: center; }
  }
`;

/* ──────────────────────────────────────────────────────────────────────────
 * Colophon (footer)
 * ────────────────────────────────────────────────────────────────────── */
const Colophon = styled.footer`
  background: ${c.white};
  border-top: 1px solid ${c.border};
  padding: 40px 32px 56px;

  .inner {
    max-width: 1200px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: 40px;
    align-items: center;

    @media (max-width: 720px) {
      grid-template-columns: 1fr;
      gap: 20px;
      text-align: center;
    }
  }

  .logo img { height: 26px; }

  .meta {
    font-family: ${body};
    font-size: 11px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: ${c.muted};
    font-weight: 500;
  }

  .links {
    display: flex;
    gap: 28px;
    font-family: ${body};
    font-size: 13px;
    font-weight: 500;

    @media (max-width: 720px) { justify-content: center; }

    a, a:hover, a:focus {
      color: ${c.heading} !important;
      text-decoration: none !important;
      transition: color 160ms ease;
    }
    a:hover {
      color: ${c.primary} !important;
    }
  }
`;

/* ──────────────────────────────────────────────────────────────────────────
 * Content
 * ────────────────────────────────────────────────────────────────────── */
const fit = [
  { n: '01', t: 'Active bidders', b: 'You respond to five or more tenders a year and know the weekly rhythm.' },
  { n: '02', t: 'Regulated industries', b: 'Government, defence, energy, consulting, or professional services.' },
  { n: '03', t: 'Collaborative approach', b: "You're comfortable with AI drafting and you doing the final edit." },
  { n: '04', t: 'An hour a week', b: 'You can spare sixty minutes a week for four weeks — most of it using the product.' },
];

const unfit = [
  { n: '01', t: 'Zero experience', b: "You've never written a tender response before. The product assumes context you don't yet have." },
  { n: '02', t: 'Production today', b: 'You need an audit-compliant, fully-signed-off system this quarter.' },
  { n: '03', t: 'Confidential only', b: "You can't share even redacted PDFs during the pilot." },
  { n: '04', t: 'Autonomous submission', b: 'You expect the AI to submit on your behalf, without a human in the loop.' },
];

const give = [
  <><strong>Full 30-day access</strong>, every feature, no restrictions.</>,
  <><strong>Direct line to the founders</strong> — usually a reply within hours.</>,
  <><strong>Three tender analyses per week</strong>, more on request.</>,
  <><strong>Early access</strong> to features before public release.</>,
  <><strong>No credit card, no contract</strong> — and no surprise invoice on day thirty-one.</>,
];

const ask = [
  <><strong>One real tender</strong> run end-to-end through the system.</>,
  <><strong>Two 30-minute feedback calls</strong> during the pilot.</>,
  <><strong>Permission to use anonymised usage patterns</strong> to improve the product.</>,
  <><strong>Your honest opinion</strong> — the unfiltered version, not the polite one.</>,
];

type Stage = { roman: string; title: string; pageRef: string; body: React.ReactNode };
const stages: Stage[] = [
  {
    roman: 'I',
    title: 'Upload a tender',
    pageRef: 'Stage 01',
    body: (
      <>
        <p>
          Drag any tender PDF into the tender bank. OCR, layout analysis, and page segmentation
          run in the background. Most 60-page documents are fully indexed in under a minute.
        </p>
        <ul>
          <li>Supported formats: PDF, DOCX, and scanned image PDFs.</li>
          <li>Upper size: one hundred megabytes per file.</li>
          <li>Redact internal notes before upload if you prefer.</li>
        </ul>
      </>
    ),
  },
  {
    roman: 'II',
    title: 'Review the structure',
    pageRef: 'Stage 02',
    body: (
      <p>
        We show you the tender as a navigable outline — sections, questions, evaluation
        criteria, financial thresholds — each with a confidence score and a link back to the
        source page. Anything yellow or red is where we want you to check our work.
      </p>
    ),
  },
  {
    roman: 'III',
    title: 'Generate grounded responses',
    pageRef: 'Stage 03',
    body: (
      <p>
        Draft answers are composed using your company's knowledge base — past submissions, case
        studies, certifications — <strong>not</strong> what the model remembers from training. Every
        paragraph cites the source it drew from, so you can audit the reasoning.
      </p>
    ),
  },
  {
    roman: 'IV',
    title: 'Score and evaluate',
    pageRef: 'Stage 04',
    body: (
      <p>
        Each response is scored across four axes — <strong>Relevance</strong>,{' '}
        <strong>Completeness</strong>, <strong>Compliance</strong>, and{' '}
        <strong>Differentiation</strong>. Scores include the rubric the model used, adjustable
        per tender to match the issuer's own criteria.
      </p>
    ),
  },
  {
    roman: 'V',
    title: 'Export or hand off',
    pageRef: 'Stage 05',
    body: (
      <p>
        Export to Word or PDF with consistent formatting, or share a read-only link with a
        colleague. Version history is preserved; nothing overwrites silently.
      </p>
    ),
  },
];

const faqs = [
  {
    q: 'Is my tender data private?',
    a: (
      <>
        <h4>Yes.</h4>
        Uploads stay within your account, are encrypted at rest, and are never used to train
        any model — ours or a third party's. If a provider (OpenAI, Anthropic, Google) routes
        your request, we pass <em>explicit no-training flags</em> on every call. You can
        request an export or deletion of all your data at any point.
      </>
    ),
  },
  {
    q: 'What happens when the thirty days end?',
    a: (
      <>
        <h4>We get on a short call.</h4>
        We ask whether it's been useful, and you choose. You can continue on a paid plan,
        pause, or walk away. No auto-charges, no dark patterns. If you walk, we send you an
        export of everything you put in.
      </>
    ),
  },
  {
    q: 'Can I use this on live tenders?',
    a: (
      <>
        <h4>Yes, with a caveat.</h4>
        <em>Always review AI outputs before submission.</em> The system is good and getting
        better, but it's not replacing your judgement during a pilot — it's earning your trust.
      </>
    ),
  },
  {
    q: 'What if the AI gets something wrong?',
    a: (
      <>
        <h4>Flag it.</h4>
        Use the feedback form at the bottom of this page, or the thumbs-down icon beside any
        generated response. Flagged outputs go straight into our review queue; the people
        reading them are the people who built the model pipeline.
      </>
    ),
  },
  {
    q: 'Can I invite a colleague?',
    a: (
      <>
        <h4>Send us their email.</h4>
        We'll add it to the pilot list, then they can register themselves at{' '}
        <em>/register</em> the same way you did. Team accounts and role-based access
        arrive in v2.
      </>
    ),
  },
  {
    q: 'I forgot my password — what now?',
    a: (
      <>
        <h4>Reset it from the sign-in page.</h4>
        Click <em>"Forgot password?"</em> on the sign-in screen, enter your email,
        and we'll send you a reset link. Or skip the password entirely and sign
        in with the one-click <strong>magic link</strong> instead — same sign-in screen,
        just the other tab.
      </>
    ),
  },
  {
    q: 'Which AI models are you running?',
    a: (
      <>
        <h4>A mix, chosen per step.</h4>
        GPT-5.5 for drafting long-form responses, Claude Sonnet 4.6 for extraction with source
        grounding, Gemini 3 Flash for scoring and short-context tasks. We swap models as new
        ones land.
      </>
    ),
  },
  {
    q: 'How do I leave the pilot?',
    a: (
      <>
        <h4>Reply with one word.</h4>
        Reply to any of our emails with <em>"done"</em>. Or just stop using it — we'll follow
        up once, then go quiet. No hoops.
      </>
    ),
  },
];

/* ──────────────────────────────────────────────────────────────────────────
 * Icons
 * ────────────────────────────────────────────────────────────────────── */
const StarGlyph: React.FC<{ filled: boolean }> = ({ filled }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round">
    <path d="M12 2.5l2.9 6.3 6.9.7-5.2 4.6 1.5 6.7L12 17.4l-6.1 3.4 1.5-6.7L2.2 9.5l6.9-.7L12 2.5z" />
  </svg>
);

const PlayGlyph: React.FC = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <path d="M8 5v14l11-7z" />
  </svg>
);

/* ──────────────────────────────────────────────────────────────────────────
 * Component
 * ────────────────────────────────────────────────────────────────────── */
type FeedbackType = 'general' | 'bug' | 'feature' | 'other';

/* Walkthrough video (YouTube) — swapped in lazily on user click */
const WALKTHROUGH_VIDEO_ID = 'ioPlEWqtsXw';
const WALKTHROUGH_THUMB = `https://i.ytimg.com/vi/${WALKTHROUGH_VIDEO_ID}/maxresdefault.jpg`;
const WALKTHROUGH_EMBED = `https://www.youtube.com/embed/${WALKTHROUGH_VIDEO_ID}?autoplay=1&rel=0&modestbranding=1`;

/* Post-pilot structured survey (Microsoft Forms) */
const POST_PILOT_SURVEY_URL =
  'https://forms.office.com/Pages/ResponsePage.aspx?id=6QiwepbvHkWNU1jYRUEbT7FjNR8b-l5NoWm8-vGGiV9UQzVDSlMwWUtESzM5QjQ3UFhLTVg2MjZMSC4u';

const PilotPage: React.FC = () => {
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('general');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [videoPlaying, setVideoPlaying] = useState(false);

  useEffect(() => {
    document.body.classList.add('pilot-dossier');
    return () => document.body.classList.remove('pilot-dossier');
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const e = params.get('email');
    if (e) setEmail(e);
    const n = params.get('name');
    if (n) setName(n);
  }, []);

  const apiBase = useMemo(
    () => process.env.REACT_APP_API_BASE_URL || 'http://localhost:2024',
    []
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim() || !email.trim() || message.trim().length < 20) {
      setError('Please include your name, email, and at least 20 characters.');
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch(`${apiBase}/pilot/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          feedback_type: feedbackType,
          rating: rating || null,
          message: message.trim(),
        }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || `Submission failed (${response.status})`);
      }
      setSubmitted(true);
      setMessage('');
      setRating(0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageGlobal />
      <PageWrap>
        {/* ── Top strip: document metadata (not marketing nav) ── */}
        <TopStrip>
          <TopStripInner>
            <div className="left">
              <Link to="/pilot" className="logo" aria-label="OpenTender AI">
                <img src="/logo-main.png" alt="OpenTender AI" />
              </Link>
              <span className="sep" />
              <span>Pilot · Edition I · 2026</span>
              <span style={{ opacity: 0.5 }}>·</span>
              <span>By invitation</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Link to="/register">Request access</Link>
              <span style={{ opacity: 0.4 }}>·</span>
              <Link to="/login">Sign in →</Link>
            </div>
          </TopStripInner>
        </TopStrip>

        {/* ── Hero: navy-dominant band ── */}
        <Hero>
          <HeroGrid>
            <HeroEdition>
              01
              <span className="sub">/ 2026 / Pilot</span>
            </HeroEdition>

            <div>
              <HeroKicker>A briefing dossier</HeroKicker>
              <HeroTitle>
                Thirty days. One real tender.
                {' '}
                <span className="hl">
                  Watched closely.
                  <svg viewBox="0 0 200 16" preserveAspectRatio="none" aria-hidden>
                    <path d="M2,11 C50,3 100,14 198,6" />
                  </svg>
                </span>
              </HeroTitle>
              <HeroLede>
                This page is the briefing for your OpenTender AI pilot — what we're building,
                what we're asking of you, and how the next thirty days will work. Read it end
                to end. It is short. It is the only reading we're asking for.
              </HeroLede>
              <HeroMeta>
                <span><span className="dot" />Dr. Salisu &amp; the OpenTender AI team</span>
                <span><span className="dot" />Reading time · 4 minutes</span>
                <span><span className="dot" />Pilot list · invite-confirmed access</span>
              </HeroMeta>
            </div>
          </HeroGrid>
        </Hero>

        {/* ── § 01 — THE PROSPECTUS — reading column with drop cap ── */}
        <Section id="prospectus">
          <Reading>
            <SectionIntro>
              <IntroNum>01</IntroNum>
              <IntroText>
                What we're building, why it exists, and what it replaces. Read
                this if you want to know <em>what you just said yes to</em>.
              </IntroText>
            </SectionIntro>
            <SectionMarker>
              <span className="num">§ 01</span>
              <span>The prospectus</span>
              <span className="rule" />
            </SectionMarker>
            <SectionH2>
              The product, in <em>plain language</em>.
            </SectionH2>
            <DropCap>
              We built OpenTender AI because responding to a tender takes a week of work that
              could take half a day — most of it not writing, but <em>reading</em>. Reading the
              brief, reading past submissions, reading competitor disclosures, reading
              evaluation rubrics buried on page forty-two. The AI reads all of it and drafts
              responses grounded in your company's actual knowledge. You still decide what
              ships.
            </DropCap>
            <Lede>
              This pilot is the first time the product is in the hands of people who didn't
              build it. It will be imperfect. Some parts of it you'll love immediately; others
              will feel rough or opinionated in the wrong direction. <strong>Both signals are
              valuable</strong> — the second one more than the first.
            </Lede>
            <Lede>
              For thirty days you'll have complete access, a direct line to the founders, and a
              small but real ask in return: tell us what's working and what isn't, bluntly,
              while it's still fresh.
            </Lede>
          </Reading>
        </Section>

        {/* ── § 02 — THE FIT CHECK — split screen (white / navy) ── */}
        <section id="fit" style={{ scrollMarginTop: 80 }}>
          <Wide style={{ paddingTop: 60, paddingBottom: 20 }}>
            <SectionIntro>
              <IntroNum>02</IntroNum>
              <IntroText>
                An honest filter. Pick your side before continuing — &ldquo;this
                isn't for me&rdquo; is a <em>fair and useful</em> answer.
              </IntroText>
            </SectionIntro>
            <SectionMarker>
              <span className="num">§ 02</span>
              <span>The fit check</span>
              <span className="rule" />
            </SectionMarker>
            <SectionH2 style={{ maxWidth: 640 }}>
              Who this is <em>really</em> for.
            </SectionH2>
            <Lede style={{ maxWidth: 620 }}>
              Pilots work best when participants actually need the product. A clean
              &ldquo;this isn't for me&rdquo; is a gift — to you and to us. The two columns
              below are honest; pick your side before reading further.
            </Lede>
          </Wide>

          <SplitWrap>
            <SplitPane $variant="fit">
              <SplitTitle>Right fit, if —</SplitTitle>
              <SplitIntro>You respond to tenders often, and you'd welcome better tools.</SplitIntro>
              <SplitList>
                {fit.map(item => (
                  <SplitItem key={item.n}>
                    <span className="idx">{item.n}</span>
                    <div>
                      <h4>{item.t}</h4>
                      <p>{item.b}</p>
                    </div>
                  </SplitItem>
                ))}
              </SplitList>
            </SplitPane>
            <SplitPane $variant="unfit">
              <SplitTitle $onDark>Not a fit, if —</SplitTitle>
              <SplitIntro $onDark>This pilot will disappoint you. Self-select out; we'll both be glad.</SplitIntro>
              <SplitList>
                {unfit.map(item => (
                  <SplitItem key={item.n} $onDark>
                    <span className="idx">{item.n}</span>
                    <div>
                      <h4>{item.t}</h4>
                      <p>{item.b}</p>
                    </div>
                  </SplitItem>
                ))}
              </SplitList>
            </SplitPane>
          </SplitWrap>
        </section>

        {/* ── § 03 — THE EXCHANGE — ledger / balance sheet ── */}
        <Section id="exchange" $variant="paper">
          <Wide>
            <SectionIntro>
              <IntroNum>03</IntroNum>
              <IntroText>
                A pilot is a trade. This is the balance sheet — what arrives in
                each column, <em>written out in full</em>.
              </IntroText>
            </SectionIntro>
            <SectionMarker>
              <span className="num">§ 03</span>
              <span>The exchange</span>
              <span className="rule" />
            </SectionMarker>
            <SectionH2 style={{ maxWidth: 620, marginBottom: 12 }}>
              What's fair, <em>on each side</em>.
            </SectionH2>
            <Lede style={{ maxWidth: 620, marginBottom: 56 }}>
              A pilot is a trade. Here's the balance sheet.
            </Lede>

            <Ledger>
              <LedgerCol>
                <LedgerHead>Credit · From us to you</LedgerHead>
                <LedgerSub>What you'll receive</LedgerSub>
                <LedgerList>
                  {give.map((item, i) => (
                    <li key={i}>
                      <span className="mark">+</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </LedgerList>
              </LedgerCol>
              <LedgerDivider className="divider" />
              <LedgerCol>
                <LedgerHead>Debit · From you to us</LedgerHead>
                <LedgerSub>What we're asking</LedgerSub>
                <LedgerList>
                  {ask.map((item, i) => (
                    <li key={i}>
                      <span className="mark">—</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </LedgerList>
              </LedgerCol>
            </Ledger>
          </Wide>
        </Section>

        {/* ── § 04 — HOW TO BEGIN — horizontal timeline ── */}
        <Section id="begin">
          <Wide>
            <SectionIntro>
              <IntroNum>04</IntroNum>
              <IntroText>
                Three stations, each under a minute. The first one is a
                button on this page — <em>the rest take less than thirty seconds</em>.
              </IntroText>
            </SectionIntro>
            <SectionMarker>
              <span className="num">§ 04</span>
              <span>How to begin</span>
              <span className="rule" />
            </SectionMarker>
            <SectionH2 style={{ maxWidth: 640 }}>
              Three stations, <em>nothing else</em>.
            </SectionH2>
            <Lede style={{ maxWidth: 620 }}>
              Your email is on the pilot list. Click below to set up your account,
              then upload a real tender. Three steps, in this order.
            </Lede>

            <Timeline>
              <Station>
                <span className="node">01</span>
                <span className="chip">Request access</span>
                <h4>Request access.</h4>
                <p>
                  Click the button below. Use the same email you registered your pilot
                  interest with — it's the one we matched against the list. You'll set
                  a password on the next page.
                </p>
                <RequestAccessCta>
                  <Link to="/register">
                    Request access
                    <span className="arrow">→</span>
                  </Link>
                </RequestAccessCta>
              </Station>
              <Station>
                <span className="node">02</span>
                <span className="chip">Set up</span>
                <h4>Set up your account.</h4>
                <p>
                  Pick a password (eight characters minimum), confirm your name and
                  organisation. Takes thirty seconds. From then on you can sign in
                  two ways: <strong>email and password</strong>, or the <strong>one-click magic link</strong>{' '}
                  we'll email you. Forgot the password? Reset it from the sign-in page.
                </p>
              </Station>
              <Station>
                <span className="node">03</span>
                <span className="chip">Real work</span>
                <h4>Upload a real tender.</h4>
                <p>
                  Start with a PDF you're actually preparing a response for. The product earns
                  its place on real documents, not demo material.
                </p>
              </Station>
            </Timeline>
          </Wide>
        </Section>

        {/* ── § 05 — THE WALKTHROUGH — video + chapter index + prose ── */}
        <Section id="walkthrough" $variant="paper">
          <Wide>
            <SectionIntro>
              <IntroNum>05</IntroNum>
              <IntroText>
                Ninety seconds of video, five chapters of detail. Read the
                chapters you're curious about — <em>skip the rest</em>.
              </IntroText>
            </SectionIntro>
            <SectionMarker>
              <span className="num">§ 05</span>
              <span>The walkthrough</span>
              <span className="rule" />
            </SectionMarker>
            <SectionH2 style={{ maxWidth: 720 }}>
              Five stages, from <em>upload to export</em>.
            </SectionH2>
            <Lede style={{ maxWidth: 620, marginBottom: 56 }}>
              A ninety-second overview sits at the top. Below it, each of the five stages is
              broken out in detail — read the ones you're curious about, skip the rest.
            </Lede>

            <VideoFrame>
              {videoPlaying ? (
                <iframe
                  src={WALKTHROUGH_EMBED}
                  title="OpenTender AI walkthrough"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <>
                  <div
                    className="thumb"
                    style={{ backgroundImage: `url("${WALKTHROUGH_THUMB}")` }}
                  />
                  <button
                    type="button"
                    className="poster"
                    onClick={() => setVideoPlaying(true)}
                    aria-label="Play walkthrough video"
                  />
                  <div className="shade" />
                  <div className="corner">Walkthrough · YouTube</div>
                  <div className="overlay">
                    <div className="play">
                      <PlayGlyph />
                    </div>
                    <div>Click to play</div>
                  </div>
                </>
              )}
            </VideoFrame>

            <ContentsBlock>
              <div className="label">Contents</div>
              <ContentsList>
                {stages.map(s => (
                  <ContentsRow key={s.roman}>
                    <span className="roman">{s.roman}.</span>
                    <span className="title">{s.title}</span>
                    <span className="page">{s.pageRef}</span>
                  </ContentsRow>
                ))}
              </ContentsList>
            </ContentsBlock>

            {stages.map(s => (
              <Chapter key={s.roman} id={`stage-${s.roman}`}>
                <div className="chapnum">Chapter {s.roman} · {s.pageRef}</div>
                <h3>{s.title}</h3>
                {s.body}
              </Chapter>
            ))}
          </Wide>
        </Section>

        {/* ── § 06 — Q&A DIALOGUE (no accordion) ── */}
        <Section id="faq">
          <Reading>
            <SectionIntro>
              <IntroNum>06</IntroNum>
              <IntroText>
                Answers came out of our first round of pilot conversations.
                If your question isn't here, the form below is <em>exactly
                the right place</em> for it.
              </IntroText>
            </SectionIntro>
            <SectionMarker>
              <span className="num">§ 06</span>
              <span>Questions asked</span>
              <span className="rule" />
            </SectionMarker>
            <SectionH2>
              Already <em>asked</em>.
            </SectionH2>
            <Lede>
              Answers came out of our first round of pilot conversations. If your question
              isn't here, the form below is exactly the right place for it.
            </Lede>
          </Reading>
          <QA style={{ marginTop: 48 }}>
            {faqs.map((f, i) => (
              <QABlock key={i}>
                <span className="q">Q.</span>
                <div className="content" style={{ paddingTop: 2 }}>
                  <h4>{f.q}</h4>
                </div>
                <span className="a">A.</span>
                <div className="content">{f.a}</div>
              </QABlock>
            ))}
          </QA>

          <TalkToHuman>
            <div className="copy">
              <div className="label">— If you'd rather talk —</div>
              <h4>Book a call.</h4>
              <p>
                Questions, concerns, guidance on where to start, or anything
                else you'd rather walk through with a person. Thirty minutes
                with Abraham. Pilot participants get priority.
              </p>
            </div>
            <a
              className="cta"
              href="https://cal.com/abraham-onoja/open-tender-ai"
              target="_blank"
              rel="noopener noreferrer"
            >
              See open times
              <span className="arrow">→</span>
            </a>
          </TalkToHuman>
        </Section>

        {/* ── § 07 — YOUR NOTES — navy closing band ── */}
        <Section id="feedback" $variant="navy">
          <Wide>
            <SectionIntro $onDark>
              <IntroNum $onDark>07</IntroNum>
              <IntroText $onDark>
                Tell us what's working and what isn't. Unfiltered is better
                than polite — <em>a human reads every submission</em> within
                one business day.
              </IntroText>
            </SectionIntro>
            <SectionMarker $onDark>
              <span className="num">§ 07</span>
              <span>Your notes</span>
              <span className="rule" />
            </SectionMarker>
            <SectionH2 $onDark style={{ maxWidth: 640 }}>
              Tell us <em>what you think</em>.
            </SectionH2>
            <Lede $onDark style={{ maxWidth: 620, marginBottom: 56 }}>
              A compliment, a bug, a feature you wish existed, or a half-formed thought. We
              read every submission within one business day.
            </Lede>

            {submitted ? (
              <SuccessBlock>
                <div className="mark">—— Received ——</div>
                <h3>Thank you.</h3>
                <p>You'll hear back from a human within one business day.</p>
              </SuccessBlock>
            ) : (
              <FormCard onSubmit={handleSubmit} noValidate>
                <FormGrid>
                  <FormRow>
                    <FieldLabel>
                      Your name
                      <Field type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Jane Doe" required />
                    </FieldLabel>
                    <FieldLabel>
                      Email address
                      <Field type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="jane@company.com" required />
                    </FieldLabel>
                  </FormRow>

                  <FieldLabel>
                    Kind of feedback
                    <RadioRow>
                      {(
                        [
                          ['general', 'General'],
                          ['bug', 'Bug'],
                          ['feature', 'Feature request'],
                          ['other', 'Other'],
                        ] as [FeedbackType, string][]
                      ).map(([v, label]) => (
                        <RadioChip key={v} $active={feedbackType === v}>
                          <input type="radio" name="ftype" value={v} checked={feedbackType === v} onChange={() => setFeedbackType(v)} />
                          {label}
                        </RadioChip>
                      ))}
                    </RadioRow>
                  </FieldLabel>

                  <FieldLabel>
                    Rating (optional)
                    <StarRow>
                      {[1, 2, 3, 4, 5].map(n => (
                        <Star
                          key={n}
                          type="button"
                          $filled={n <= (hoverRating || rating)}
                          onClick={() => setRating(n === rating ? 0 : n)}
                          onMouseEnter={() => setHoverRating(n)}
                          onMouseLeave={() => setHoverRating(0)}
                          aria-label={`${n} out of 5`}
                        >
                          <StarGlyph filled={n <= (hoverRating || rating)} />
                        </Star>
                      ))}
                    </StarRow>
                  </FieldLabel>

                  <FieldLabel>
                    Your message
                    <TextArea value={message} onChange={e => setMessage(e.target.value)} placeholder="What do you want us to know?" required minLength={20} />
                  </FieldLabel>

                  {error && <ErrorLine>{error}</ErrorLine>}

                  <FormFoot>
                    <span className="hint">Submissions go directly to the founders.</span>
                    <SubmitBtn type="submit" disabled={submitting}>
                      {submitting ? 'Sending' : 'File report'}
                    </SubmitBtn>
                  </FormFoot>
                </FormGrid>
              </FormCard>
            )}
          </Wide>
        </Section>

        {/* ── § 08 — THE CLOSING SURVEY — external Microsoft Forms link ── */}
        <Section id="survey" $variant="paper">
          <Wide>
            <SectionIntro>
              <IntroNum>08</IntroNum>
              <IntroText>
                A longer conversation, at the end. The notes above are for
                what's fresh; this one is for what you <em>sit with</em> after
                a month of using the product.
              </IntroText>
            </SectionIntro>
            <SectionMarker>
              <span className="num">§ 08</span>
              <span>The closing survey</span>
              <span className="rule" />
            </SectionMarker>
            <SectionH2 style={{ maxWidth: 720 }}>
              When the pilot ends, <em>sit with it</em>.
            </SectionH2>
            <Lede style={{ maxWidth: 620, marginBottom: 40 }}>
              On day thirty we'll email you a link to a structured survey.
              It's hosted on Microsoft Forms, takes about ten minutes, and
              asks the longer questions — where it earned its place in your
              process, where it didn't, what you'd pay for it, and what you'd
              change. This is the feedback that shapes v2.
            </Lede>

            <SurveyCard>
              <div className="copy">
                <h4>The post-pilot survey</h4>
                <p>
                  Open it anytime, but it's most useful once you've run at
                  least one real tender through the system.
                </p>
                <div className="meta">
                  <span>~10 minutes</span>
                  <span>Microsoft Forms</span>
                  <span>Reminder sent on day 28</span>
                </div>
              </div>
              <SurveyCta
                href={POST_PILOT_SURVEY_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open the survey
                <span className="arrow">→</span>
              </SurveyCta>
            </SurveyCard>
          </Wide>
        </Section>

        {/* ── Colophon ── */}
        <Colophon>
          <div className="inner">
            <div className="logo">
              <img src="/logo-main.png" alt="OpenTender AI" />
            </div>
            <div className="meta">
              Filed 2026 · Ref · Pilot-01 · Circulation by invitation
            </div>
            <div className="links">
              <a href="https://www.opentender.ai/" target="_blank" rel="noopener noreferrer">Main site</a>
              <Link to="/contact">Contact</Link>
              <a
                href="https://cal.com/abraham-onoja/open-tender-ai"
                target="_blank"
                rel="noopener noreferrer"
              >
                Book a call
              </a>
              <Link to="/register">Request access</Link>
              <Link to="/login">Sign in</Link>
            </div>
          </div>
        </Colophon>
      </PageWrap>
    </>
  );
};

export default PilotPage;
